import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CommandPaletteProvider } from './context/CommandPaletteContext';
import { Toaster } from './components/ui/sonner';
import AppShell from './components/AppShell';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage, TrustDevicePage } from './pages/PasswordResetPages';
import SharedEstimatePage from './pages/SharedEstimatePage';
import EstimatorPage from './pages/EstimatorPage';
import RequirementsFormPage from './pages/RequirementsFormPage';
import EstimateMaturityPage from './pages/EstimateMaturityPage';
import EstimatorNewPage from './pages/EstimatorNewPage';
import StakeholderFeedbackPage from './pages/StakeholderFeedbackPage';
import ChangeRequestDecisionPage from './pages/ChangeRequestDecisionPage';
// HistoryPage import removed — /history now redirects to /workspaces?recent=1 (Feb 2026 UX pass)
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import ComparePage from './pages/ComparePage';
import PlannerPage from './pages/PlannerPage';
import SimulatorPage from './pages/SimulatorPage';
import WinnerApprovalPage from './pages/WinnerApprovalPage';
import InsightsPage from './pages/InsightsPage';
import VerificationHealthPage from './pages/VerificationHealthPage';
import EstimateOutputPage from './pages/EstimateOutputPage';
import MaturityHealthPage from './pages/MaturityHealthPage';
import ProfilePage from './pages/ProfilePage';
import PlanningConfigPage from './pages/PlanningConfigPage';
import MathReferencePage from './pages/MathReferencePage';
import UserGuidePage from './pages/UserGuidePage';
import MyEstimatesPage from './pages/MyEstimatesPage';
import TeamAvailabilityCalendarPage from './pages/TeamAvailabilityCalendarPage';
import WorkspaceOverviewPage from './pages/WorkspaceOverviewPage';
import QuotePreviewPage from './pages/QuotePreviewPage';
import QuotePrintPage from './pages/QuotePrintPage';
import CompareQuotesPage from './pages/CompareQuotesPage';
import QuoteHubPage from './pages/QuoteHubPage';
import RfpLibraryPage from './pages/RfpLibraryPage';
import RfpNarrativeLibraryPage from './pages/RfpNarrativeLibraryPage';
import RfpResponsePage from './pages/RfpResponsePage';
import RfpSharePage from './pages/RfpSharePage';
import RfpPreviewSharePage from './pages/RfpPreviewSharePage';
import ApprovalPage from './pages/ApprovalPage';
import PreferencesPage from './pages/PreferencesPage';
import StakeholderPortalPage from './pages/StakeholderPortalPage';
import AuthCallback from './pages/AuthCallback';
import SOWEditorPage from './pages/SOWEditorPage';
import WorkspaceDashboardPage from './pages/WorkspaceDashboardPage';
import QuickEstimatePage from './pages/QuickEstimatePage';
import EstimateWorkspaceLayout from './components/EstimateWorkspaceLayout';
import EstimateOverviewTab from './pages/workspace/EstimateOverviewTab';
import WorkspaceActivityTab from './pages/workspace/WorkspaceActivityTab';
import WorkspaceCommentsTab from './pages/workspace/WorkspaceCommentsTab';
import WorkspaceDocumentsTab from './pages/workspace/WorkspaceDocumentsTab';
import DesignPackTab from './pages/workspace/DesignPackTab';
import ProductPricingTab from './pages/workspace/ProductPricingTab';
import PinnedStatusNotifier from './components/PinnedStatusNotifier';

import './App.css';

// Thin wrappers for workspace context
function WorkspacePlannerWrapper() {
  const { estimateId } = useParams();
  return <PlannerPage initialEstimateId={estimateId} />;
}
function WorkspaceCompareWrapper() {
  const { estimateId } = useParams();
  return <ComparePage initialEstimateId={estimateId} />;
}

// Protected Route wrapper with AppShell
function ProtectedRoute({ children, noShell = false, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#4ABFCF] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin-only route guard — allow admin, cs, cro, cco, super_admin
  const adminRoles = ['admin', 'cs', 'cro', 'cco', 'super_admin'];
  if (adminOnly && !adminRoles.includes(user.role)) {
    return <Navigate to={user.role === 'user' ? '/workspaces' : '/estimator'} replace />;
  }

  return noShell ? (
    <>
      <PinnedStatusNotifier />
      {children}
    </>
  ) : (
    <AppShell>
      <PinnedStatusNotifier />
      {children}
    </AppShell>
  );
}

// Blocks the view-only USER role from restricted surfaces (v273):
// Compare, Quote, Design Pack, Requirements, Documents, Maturity,
// Activity, Comments. Other roles pass straight through.
function ViewerBlocked({ children }) {
  const { user } = useAuth();
  if (user?.role === 'user') return <Navigate to="/workspaces" replace />;
  return children;
}

// Guest Route wrapper (redirect to estimator if already logged in)
function GuestRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#4ABFCF] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === 'user' ? '/workspaces' : '/estimator'} replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=') || location.search?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
      <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/trust-device" element={<TrustDevicePage />} />
      {/* /register removed (Feb 2026): self-registration disabled —
          Super Admins provision users via /api/admin/users. Redirect
          legacy bookmarks / cached links to /login. */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/feedback/:sendId" element={<StakeholderFeedbackPage />} />
      <Route path="/change-request/:crId" element={<ChangeRequestDecisionPage />} />
      <Route path="/estimator" element={<ProtectedRoute><EstimatorPage /></ProtectedRoute>} />
      <Route path="/estimator/:id/requirements" element={<ProtectedRoute><ViewerBlocked><RequirementsFormPage /></ViewerBlocked></ProtectedRoute>} />
      <Route path="/estimator/:id/maturity" element={<ProtectedRoute><ViewerBlocked><EstimateMaturityPage /></ViewerBlocked></ProtectedRoute>} />
      <Route path="/estimator/new" element={<ProtectedRoute><EstimatorNewPage /></ProtectedRoute>} />
      <Route path="/estimator/output" element={<ProtectedRoute><EstimateOutputPage /></ProtectedRoute>} />
      <Route path="/estimator/quote/:estimateId" element={<ProtectedRoute><QuotePreviewPage /></ProtectedRoute>} />
      <Route path="/estimator/quote/:estimateId/print" element={<ProtectedRoute noShell><QuotePrintPage /></ProtectedRoute>} />
      <Route path="/quotes" element={<ProtectedRoute><QuoteHubPage /></ProtectedRoute>} />
      <Route path="/rfp-library" element={<ProtectedRoute><RfpLibraryPage /></ProtectedRoute>} />
      <Route path="/rfp-narrative-library" element={<ProtectedRoute><RfpNarrativeLibraryPage /></ProtectedRoute>} />
      <Route path="/rfp-response" element={<ProtectedRoute><ViewerBlocked><RfpResponsePage /></ViewerBlocked></ProtectedRoute>} />
      <Route path="/rfp-share/:token" element={<RfpSharePage />} />
      <Route path="/rfp-preview/:token" element={<RfpPreviewSharePage />} />
      <Route path="/compare-quotes" element={<ProtectedRoute><CompareQuotesPage /></ProtectedRoute>} />
      {/* /history → /workspaces?recent=1 (UX pass Feb 2026 — Workspaces is
          the new home for browsing past estimates; History route kept as
          a redirect so old bookmarks still resolve). */}
      <Route path="/history" element={<Navigate to="/workspaces?recent=1" replace />} />
      <Route path="/workspaces" element={<ProtectedRoute><WorkspaceDashboardPage /></ProtectedRoute>} />
      <Route path="/workspace-overview" element={<ProtectedRoute><WorkspaceOverviewPage /></ProtectedRoute>} />
      <Route path="/my-estimates" element={<ProtectedRoute><ViewerBlocked><MyEstimatesPage /></ViewerBlocked></ProtectedRoute>} />
      <Route path="/quick-estimate" element={<ProtectedRoute><QuickEstimatePage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><SettingsPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/compare" element={<ProtectedRoute><ViewerBlocked><ComparePage /></ViewerBlocked></ProtectedRoute>} />
      <Route path="/planner" element={<ProtectedRoute><ViewerBlocked><PlannerPage /></ViewerBlocked></ProtectedRoute>} />
      <Route path="/simulator" element={<ProtectedRoute><SimulatorPage /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><ViewerBlocked><InsightsPage /></ViewerBlocked></ProtectedRoute>} />
      <Route path="/verification-health" element={<ProtectedRoute><ViewerBlocked><VerificationHealthPage /></ViewerBlocked></ProtectedRoute>} />
      <Route path="/maturity" element={<ProtectedRoute><MaturityHealthPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      <Route path="/admin/planning-config" element={<ProtectedRoute adminOnly><PlanningConfigPage /></ProtectedRoute>} />
      <Route path="/team-availability" element={<ProtectedRoute adminOnly><TeamAvailabilityCalendarPage /></ProtectedRoute>} />
      <Route path="/sow/new" element={<ProtectedRoute adminOnly><SOWEditorPage /></ProtectedRoute>} />
      <Route path="/sow/:sowId" element={<ProtectedRoute adminOnly><SOWEditorPage /></ProtectedRoute>} />
      <Route path="/math-reference" element={<ProtectedRoute><MathReferencePage /></ProtectedRoute>} />
      <Route path="/user-guide" element={<ProtectedRoute><UserGuidePage /></ProtectedRoute>} />
      <Route path="/approval/:token" element={<ApprovalPage />} />
      <Route path="/winner-approval/:token" element={<WinnerApprovalPage />} />
      <Route path="/preferences/:token" element={<PreferencesPage />} />
      <Route path="/portal/:token" element={<StakeholderPortalPage />} />
      <Route path="/shared/:token" element={<SharedEstimatePage />} />

      {/* Estimate Workspace — contextual sub-nav for a single estimate */}
      <Route path="/workspace/:estimateId" element={<ProtectedRoute><EstimateWorkspaceLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<EstimateOverviewTab />} />
        <Route path="compare" element={<ViewerBlocked><WorkspaceCompareWrapper /></ViewerBlocked>} />
        <Route path="planner" element={<WorkspacePlannerWrapper />} />
        <Route path="product-pricing" element={<ViewerBlocked><ProductPricingTab /></ViewerBlocked>} />
        <Route path="quote" element={<ViewerBlocked><QuotePreviewPage /></ViewerBlocked>} />
        <Route path="design-pack" element={<ViewerBlocked><DesignPackTab /></ViewerBlocked>} />
        <Route path="activity" element={<ViewerBlocked><WorkspaceActivityTab /></ViewerBlocked>} />
        <Route path="comments" element={<ViewerBlocked><WorkspaceCommentsTab /></ViewerBlocked>} />
        <Route path="documents" element={<ViewerBlocked><WorkspaceDocumentsTab /></ViewerBlocked>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppToaster() {
  const { impersonation } = useAuth();
  return <Toaster position="top-right" offset={impersonation ? 56 : 16} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CommandPaletteProvider>
          <AppRouter />
          <AppToaster />
        </CommandPaletteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
